import { Auth } from "aws-amplify";
import { clearData } from "../pages/account/profileSlice";
import { logout } from "../pages/auth/registrationSlice";
import { setEmailError } from "../pages/auth/loginSlice";
import { dataService } from "../services/dataService";

export const releaseSuperAdminSessionIfHeld = async () => {
    const sellerEmail = sessionStorage.getItem('selectedSellerEmail');
    if (sellerEmail) {
        await dataService.releaseSuperAdminSession(sellerEmail).catch(() => {});
    }
};

/**
 * Applies a pending Super Admin impersonation request on ANY sign-in completion
 * (explicit form submit or an Amplify Hub "signIn" event from a silently
 * restored/renewed session), so impersonation is never bypassed depending on
 * which code path happened to complete the Cognito sign-in.
 *
 * Returns 'not-applicable' when the signed-in user isn't a super-admin or there's
 * no pending impersonation target (caller should proceed with normal login),
 * 'success' when impersonation was applied, or 'error' when the lock could not be
 * acquired (an error message has already been dispatched; caller should stop).
 */
export const applySuperAdminImpersonation = async ({ idTokenPayload, dispatch }) => {
    const groups = idTokenPayload?.['cognito:groups'] || [];
    const groupList = Array.isArray(groups) ? groups : groups.split(',');
    const impersonateEmail = sessionStorage.getItem('impersonateEmail');
    if (!groupList.includes('super-admin') || !impersonateEmail) {
        return 'not-applicable';
    }

    let sessionResult = await dataService.acquireSuperAdminSession(impersonateEmail);

    if (sessionResult.error && sessionResult.status === 409) {
        const lockedBy = sessionResult.data?.locked_by;
        const isSelfLock = lockedBy && [idTokenPayload?.email, idTokenPayload?.['cognito:username']].includes(lockedBy);
        if (isSelfLock) {
            // Stale lock from this same admin's own earlier session (e.g. tab closed
            // without the release call making it through) — clear it and retry once.
            await dataService.releaseSuperAdminSession(impersonateEmail).catch(() => {});
            sessionResult = await dataService.acquireSuperAdminSession(impersonateEmail);
        }
    }

    if (sessionResult.error) {
        if (sessionResult.status === 409) {
            const lockedBy = sessionResult.data?.locked_by || 'another admin';
            dispatch(setEmailError(
                `This seller is currently being managed by ${lockedBy}. Please try again later or contact them.`
            ));
        } else {
            dispatch(setEmailError('Failed to acquire super-admin session. Please try again.'));
        }
        return 'error';
    }

    sessionStorage.setItem('selectedSellerEmail', impersonateEmail);
    sessionStorage.removeItem('impersonateEmail');
    window.dispatchEvent(new CustomEvent('superAdminSessionReady'));
    return 'success';
};

const checkUser = async () => {
    try {
        const user = await Auth.currentAuthenticatedUser();
        if(!user){
            await releaseSuperAdminSessionIfHeld();
            sessionStorage.removeItem('selectedSellerEmail');
            localStorage.clear();
            Auth.signOut();
            window.location.href = "/login";
        }
    } catch (err) {
        console.log(err, 'error...');
        await releaseSuperAdminSessionIfHeld();
        sessionStorage.removeItem('selectedSellerEmail');
        localStorage.clear();
        Auth.signOut();
        window.location.href = "/login";
    }
};


/**
 * The function `authHeader` returns an object containing an Authorization header with a JWT token.
 * @returns an object containing the Authorization header with the JWT token.
 */
export async function authHeader() {
// Call checkUser in a try-catch block, but skip on login page to avoid race condition
// during login where Auth.currentAuthenticatedUser() throws before session is committed
    if (window.location.pathname !== '/login') {
        checkUser()
    }

    const idToken = await Auth.currentSession()
    const headers = {
        Authorization: `Bearer ${idToken.getIdToken().getJwtToken()}`,
    }
    return headers;
}


export const isSuperAdminSession = () =>
    Boolean(sessionStorage.getItem('selectedSellerEmail'));